import React, { useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, Modal, Pressable } from 'react-native';
import { CHAT_MOBILE_COLORS } from '@/constants/residents/mobile/chatColors';
import { SVG_COMPONENTS } from '@/assets/admin';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { MESSAGE_SORT_OPTIONS, getSortOptionLabel, type MessageSortOption } from '@/constants/residents/messages';

const SearchIcon = RESIDENTS_SVG_COMPONENTS.MAGNIFYING_GLASS;
const PencilIcon = SVG_COMPONENTS.PENCIL;
const ChevronDownIcon = RESIDENTS_SVG_COMPONENTS.CHEVRON_DOWN;

// Re-export for backwards compatibility
export type SortOption = MessageSortOption;

interface ChatListHeaderProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onComposePress: () => void;
  sortOption: SortOption;
  onSortChange: (option: SortOption) => void;
  hideSortRow?: boolean;
}

/**
 * Header component for chat list screen
 * Contains search bar, compose button, and sort dropdown
 */
export const ChatListHeader: React.FC<ChatListHeaderProps> = ({
  searchQuery,
  onSearchChange,
  onComposePress,
  sortOption,
  onSortChange,
  hideSortRow = false,
}) => {
  const [isSortModalVisible, setSortModalVisible] = useState(false);

  const currentSortLabel = getSortOptionLabel(sortOption);

  const handleSortSelect = (option: SortOption) => {
    onSortChange(option);
    setSortModalVisible(false);
  };

  return (
    <View style={styles.container}>
      {/* Search bar and compose button row */}
      <View style={styles.searchRow}>
        <View style={styles.searchContainer}>
          <SearchIcon width={20} height={20} color={CHAT_MOBILE_COLORS.textMuted} />
          <TextInput
            style={styles.searchInput}
            placeholder="Search"
            placeholderTextColor={CHAT_MOBILE_COLORS.textPlaceholder}
            value={searchQuery}
            onChangeText={onSearchChange}
          />
        </View>
        <TouchableOpacity
          style={styles.composeButton}
          onPress={onComposePress}
          activeOpacity={0.8}
        >
          <PencilIcon width={20} height={20} color={CHAT_MOBILE_COLORS.iconDark} />
        </TouchableOpacity>
      </View>

      {/* Sort dropdown - hidden in empty state */}
      {!hideSortRow && (
        <View style={styles.sortRow}>
          <TouchableOpacity
            style={styles.sortButton}
            onPress={() => setSortModalVisible(true)}
            activeOpacity={0.7}
          >
            <Text style={styles.sortText}>{currentSortLabel}</Text>
            <ChevronDownIcon width={20} height={20} color={CHAT_MOBILE_COLORS.textMuted} />
          </TouchableOpacity>
        </View>
      )}

      {/* Sort options modal */}
      <Modal
        visible={isSortModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setSortModalVisible(false)}
      >
        <Pressable style={styles.modalOverlay} onPress={() => setSortModalVisible(false)}>
          <View style={styles.sortModal}>
            {MESSAGE_SORT_OPTIONS.map((option) => (
              <TouchableOpacity
                key={option.key}
                style={[
                  styles.sortOption,
                  sortOption === option.key && styles.sortOptionSelected,
                ]}
                onPress={() => handleSortSelect(option.key)}
              >
                <Text
                  style={[
                    styles.sortOptionText,
                    sortOption === option.key && styles.sortOptionTextSelected,
                  ]}
                >
                  {option.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </Pressable>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 24,
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 20,
    paddingVertical: 24,
    borderBottomWidth: 1,
    borderBottomColor: CHAT_MOBILE_COLORS.divider,
  },
  searchContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: CHAT_MOBILE_COLORS.searchBackground,
    borderRadius: 8,
    height: 40,
    paddingHorizontal: 14,
    gap: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: CHAT_MOBILE_COLORS.textPrimary,
    lineHeight: 21,
    padding: 0,
  },
  composeButton: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: CHAT_MOBILE_COLORS.accentPink,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sortRow: {
    paddingTop: 20,
    paddingBottom: 10,
  },
  sortButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  sortText: {
    fontSize: 12,
    fontWeight: '300',
    color: CHAT_MOBILE_COLORS.textMuted,
    lineHeight: 18,
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    paddingHorizontal: 24,
  },
  sortModal: {
    backgroundColor: CHAT_MOBILE_COLORS.cardBackground,
    borderRadius: 12,
    overflow: 'hidden',
  },
  sortOption: {
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: CHAT_MOBILE_COLORS.divider,
  },
  sortOptionSelected: {
    backgroundColor: 'rgba(238, 241, 244, 0.05)',
  },
  sortOptionText: {
    fontSize: 14,
    color: CHAT_MOBILE_COLORS.textPrimary,
    lineHeight: 21,
  },
  sortOptionTextSelected: {
    fontWeight: '600',
  },
});

export default ChatListHeader;
